/**
 * 새 세모 심벌(피그마 «final logo / 최종안», node 624-1534)의 붓 자국 데이터.
 *
 * 심벌은 반투명 원 53개를 사선으로 겹쳐 찍은 획 두 개다(아래 획 → 위 획 순서로 겹친다).
 * 원 하나하나의 위치·불투명도·그러데이션 세 색을 피그마 원본에서 그대로 옮겼다.
 * 좌표는 심벌 가운데가 0,0, 심벌 높이가 100 이 되게 줄였다. 원은 붓이 지나간 순서(왼쪽 아래 → 오른쪽 위)다.
 */

/** [가운데 x, 가운데 y, 불투명도, 밝은 색, 중간 색, 짙은 색] */
export type BrushStamp = readonly [
  number,
  number,
  number,
  string,
  string,
  string,
];

export interface BrushStroke {
  /** 원 반지름 */
  r: number;
  stamps: readonly BrushStamp[];
}

/** 심벌 너비(높이 100 기준) */
export const SEMO_MARK_WIDTH = 66.62;
export const SEMO_MARK_HEIGHT = 100;

/** 붓이 나아가는 방향(단위 벡터, 왼쪽 아래 → 오른쪽 위) */
export const SEMO_STROKE_DIR = { x: 0.6676, y: -0.7446 };

export const SEMO_LOWER_STROKE: BrushStroke = {
  r: 16.561,
  stamps: [
    [-16.75, 33.44, 0.2, "#BD54F5", "#B047F2", "#A640F2"],
    [-16.17, 32.79, 0.2, "#BD54F5", "#B047F2", "#A640F2"],
    [-15.58, 32.14, 0.2, "#BB53F4", "#AF46F2", "#A53FF2"],
    [-15.0, 31.49, 0.2, "#BA52F4", "#AE45F1", "#A33EF1"],
    [-14.42, 30.84, 0.2, "#B951F3", "#AC44F0", "#A23DF0"],
    [-13.83, 30.19, 0.2, "#B850F2", "#AB43F0", "#A13CF0"],
    [-13.25, 29.54, 0.2, "#B74FF2", "#AA42EF", "#A03BEF"],
    [-12.67, 28.89, 0.2, "#B54EF1", "#A941EF", "#9E3AEF"],
    [-12.08, 28.24, 0.2, "#B44DF1", "#A740EE", "#9D39EE"],
    [-11.5, 27.59, 0.2, "#B34CF0", "#A63FEE", "#9C38EE"],
    [-10.92, 26.94, 0.2, "#B24BF0", "#A53EED", "#9B37ED"],
    [-10.34, 26.29, 0.2, "#B04AEF", "#A43DEC", "#9A36EC"],
    [-9.75, 25.64, 0.2, "#AF49EE", "#A33CEC", "#9835EC"],
    [-9.17, 24.99, 0.2, "#AE48EE", "#A13BEB", "#9734EB"],
    [-8.59, 24.34, 0.2, "#AE48EE", "#A13BEB", "#9734EB"],
    [-8.0, 23.68, 0.2, "#AD47ED", "#A03AEB", "#9633EB"],
    [-7.42, 23.03, 0.2, "#AC46ED", "#9F39EA", "#9532EA"],
    [-6.84, 22.39, 0.2, "#AA45EC", "#9E38E9", "#9331E9"],
    [-6.25, 21.73, 0.2, "#A944EB", "#9C37E9", "#9230E9"],
    [-5.67, 21.08, 0.2, "#A843EB", "#9B36E8", "#912FE8"],
    [-5.09, 20.43, 0.2, "#A742EA", "#9A35E8", "#902EE8"],
    [-4.51, 19.79, 0.2, "#A541EA", "#9934E7", "#8F2DE7"],
    [-3.92, 19.14, 0.2, "#A440E9", "#9833E7", "#8D2CE7"],
    [-3.34, 18.48, 0.2, "#A33FE9", "#9632E6", "#8C2BE6"],
    [-2.76, 17.83, 0.2, "#A23EE8", "#9531E5", "#8B2AE5"],
    [-2.17, 17.18, 0.2, "#A13DE7", "#9430E5", "#8A29E5"],
    [-1.59, 16.53, 0.2, "#9F3CE7", "#932FE4", "#8828E4"],
    [-1.01, 15.88, 0.2, "#9F3CE7", "#932FE4", "#8828E4"],
    [-0.43, 15.23, 0.2, "#9E3BE6", "#912EE4", "#8727E4"],
    [0.16, 14.58, 0.2, "#9D3AE6", "#902DE3", "#8626E3"],
    [0.74, 13.93, 0.2, "#9C39E5", "#8F2CE2", "#8524E2"],
    [1.32, 13.28, 0.2, "#9A38E4", "#8E2BE2", "#8423E2"],
    [1.91, 12.63, 0.2, "#9937E4", "#8D2AE1", "#8222E1"],
    [2.49, 11.98, 0.2, "#9836E3", "#8B29E1", "#8121E1"],
    [3.07, 11.33, 0.2, "#9735E3", "#8A28E0", "#8020E0"],
    [3.66, 10.68, 0.2, "#9634E2", "#8927E0", "#7F1FE0"],
    [4.24, 10.03, 0.2, "#9433E2", "#8826DF", "#7D1EDF"],
    [4.82, 9.38, 0.3, "#9332E1", "#8625DE", "#7C1DDE"],
    [5.4, 8.73, 0.3, "#9231E0", "#8524DE", "#7B1CDE"],
    [5.99, 8.08, 0.3, "#9130E0", "#8423DD", "#7A1BDD"],
    [6.57, 7.43, 0.3, "#9130E0", "#8423DD", "#7A1BDD"],
    [7.15, 6.78, 0.3, "#8F2FDF", "#8322DD", "#791ADD"],
    [7.74, 6.13, 0.3, "#8E2EDF", "#8221DC", "#7719DC"],
    [8.32, 5.48, 0.3, "#8D2DDE", "#8020DB", "#7618DB"],
    [8.9, 4.83, 0.3, "#8C2CDD", "#7F1FDB", "#7517DB"],
    [9.49, 4.18, 0.3, "#8B2BDD", "#7E1EDA", "#7416DA"],
    [10.07, 3.53, 0.3, "#892ADC", "#7D1DDA", "#7215DA"],
    [10.65, 2.88, 0.3, "#8829DC", "#7B1CD9", "#7114D9"],
    [11.23, 2.23, 0.3, "#8728DB", "#7A1BD9", "#7013D9"],
    [11.82, 1.58, 0.3, "#8627DB", "#791AD8", "#6F12D8"],
    [12.4, 0.93, 0.3, "#8426DA", "#7819D7", "#6E11D7"],
    [12.98, 0.28, 0.3, "#8325D9", "#7718D7", "#6C10D7"],
    [13.57, -0.37, 0.3, "#8224D9", "#7517D6", "#6B0FD6"],
  ],
};

export const SEMO_UPPER_STROKE: BrushStroke = {
  r: 16.862,
  stamps: [
    [-15.64, 0.15, 0.2, "#BD54F5", "#B047F2", "#A640F2"],
    [-15.03, -0.49, 0.2, "#BD54F5", "#B047F2", "#A640F2"],
    [-14.41, -1.13, 0.2, "#BB53F4", "#AF46F2", "#A53FF2"],
    [-13.79, -1.77, 0.2, "#BA52F4", "#AE45F1", "#A33EF1"],
    [-13.18, -2.41, 0.2, "#B951F3", "#AC44F0", "#A23DF0"],
    [-12.56, -3.06, 0.2, "#B850F2", "#AB43F0", "#A13CF0"],
    [-11.94, -3.7, 0.2, "#B74FF2", "#AA42EF", "#A03BEF"],
    [-11.32, -4.34, 0.2, "#B54EF1", "#A941EF", "#9E3AEF"],
    [-10.71, -4.97, 0.2, "#B44DF1", "#A740EE", "#9D39EE"],
    [-10.09, -5.61, 0.2, "#B34CF0", "#A63FEE", "#9C38EE"],
    [-9.47, -6.25, 0.2, "#B24BF0", "#A53EED", "#9B37ED"],
    [-8.86, -6.89, 0.2, "#B04AEF", "#A43DEC", "#9A36EC"],
    [-8.24, -7.54, 0.2, "#AF49EE", "#A33CEC", "#9835EC"],
    [-7.62, -8.17, 0.2, "#AE48EE", "#A13BEB", "#9734EB"],
    [-7.0, -8.82, 0.2, "#AE48EE", "#A13BEB", "#9734EB"],
    [-6.39, -9.46, 0.2, "#AD47ED", "#A03AEB", "#9633EB"],
    [-5.77, -10.1, 0.2, "#AC46ED", "#9F39EA", "#9532EA"],
    [-5.15, -10.73, 0.2, "#AA45EC", "#9E38E9", "#9331E9"],
    [-4.54, -11.38, 0.2, "#A944EB", "#9C37E9", "#9230E9"],
    [-3.92, -12.02, 0.2, "#A843EB", "#9B36E8", "#912FE8"],
    [-3.3, -12.66, 0.2, "#A742EA", "#9A35E8", "#902EE8"],
    [-2.68, -13.3, 0.2, "#A541EA", "#9934E7", "#8F2DE7"],
    [-2.07, -13.94, 0.2, "#A440E9", "#9833E7", "#8D2CE7"],
    [-1.45, -14.58, 0.2, "#A33FE9", "#9632E6", "#8C2BE6"],
    [-0.83, -15.22, 0.2, "#A23EE8", "#9531E5", "#8B2AE5"],
    [-0.22, -15.86, 0.2, "#A13DE7", "#9430E5", "#8A29E5"],
    [0.4, -16.5, 0.2, "#9F3CE7", "#932FE4", "#8828E4"],
    [1.02, -17.14, 0.2, "#9F3CE7", "#932FE4", "#8828E4"],
    [1.64, -17.78, 0.2, "#9E3BE6", "#912EE4", "#8727E4"],
    [2.25, -18.42, 0.2, "#9D3AE6", "#902DE3", "#8626E3"],
    [2.87, -19.06, 0.2, "#9C39E5", "#8F2CE2", "#8524E2"],
    [3.49, -19.7, 0.2, "#9A38E4", "#8E2BE2", "#8423E2"],
    [4.1, -20.34, 0.2, "#9937E4", "#8D2AE1", "#8222E1"],
    [4.72, -20.98, 0.2, "#9836E3", "#8B29E1", "#8121E1"],
    [5.34, -21.62, 0.2, "#9735E3", "#8A28E0", "#8020E0"],
    [5.96, -22.26, 0.2, "#9634E2", "#8927E0", "#7F1FE0"],
    [6.57, -22.9, 0.2, "#9433E2", "#8826DF", "#7D1EDF"],
    [7.19, -23.54, 0.3, "#9332E1", "#8625DE", "#7C1DDE"],
    [7.81, -24.18, 0.3, "#9231E0", "#8524DE", "#7B1CDE"],
    [8.42, -24.82, 0.3, "#9130E0", "#8423DD", "#7A1BDD"],
    [9.04, -25.46, 0.3, "#9130E0", "#8423DD", "#7A1BDD"],
    [9.66, -26.1, 0.3, "#8F2FDF", "#8322DD", "#791ADD"],
    [10.28, -26.74, 0.3, "#8E2EDF", "#8221DC", "#7719DC"],
    [10.89, -27.38, 0.3, "#8D2DDE", "#8020DB", "#7618DB"],
    [11.51, -28.02, 0.3, "#8C2CDD", "#7F1FDB", "#7517DB"],
    [12.13, -28.66, 0.3, "#8B2BDD", "#7E1EDA", "#7416DA"],
    [12.74, -29.3, 0.3, "#892ADC", "#7D1DDA", "#7215DA"],
    [13.36, -29.94, 0.3, "#8829DC", "#7B1CD9", "#7114D9"],
    [13.98, -30.58, 0.3, "#8728DB", "#7A1BD9", "#7013D9"],
    [14.6, -31.22, 0.3, "#8627DB", "#791AD8", "#6F12D8"],
    [15.21, -31.86, 0.3, "#8426DA", "#7819D7", "#6E11D7"],
    [15.83, -32.5, 0.3, "#8325D9", "#7718D7", "#6C10D7"],
    [16.45, -33.14, 0.3, "#8224D9", "#7517D6", "#6B0FD6"],
  ],
};
